import React, { useState, useEffect } from 'react';
import { FileText, Eye, Download, X, ZoomIn, ZoomOut, Code, File, Copy, Check, AlertCircle, Plus, Upload, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';
import './OriginalTemplates.css';

const OriginalTemplates = () => {
  // View state
  const [amcs, setAmcs] = useState([]);
  const [selectedAmc, setSelectedAmc] = useState(null);
  const [pdfTemplates, setPdfTemplates] = useState([]);
  const [jsonTemplates, setJsonTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('pdf');
  const [usingFallback, setUsingFallback] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(true);

  // Admin state
  const [adminExpandedAmc, setAdminExpandedAmc] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAmcName, setNewAmcName] = useState('');
  const [newAmcDescription, setNewAmcDescription] = useState('');
  const [creatingAmc, setCreatingAmc] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadAmc, setUploadAmc] = useState(null);
  const [uploadType, setUploadType] = useState('pdf');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Preview state
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [jsonPreview, setJsonPreview] = useState(null);
  const [jsonContent, setJsonContent] = useState(null);
  const [copied, setCopied] = useState(false);

  useMountEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please login to access this page');
      setLoading(false);
      return;
    }
    fetchAmcs();
  });

  useEffect(() => {
    if (selectedAmc) {
      fetchTemplates(selectedAmc._id);
    }
  }, [selectedAmc]);

  const fetchAmcs = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/amcs');
      const amcsWithTemplates = await Promise.all(
        (data.amcs || []).map(async (amc) => {
          try {
            const pdfData = await apiCall(`/api/amcs/${amc._id}/pdf-templates`);
            const jsonData = await apiCall(`/api/amcs/${amc._id}/json-templates`);
            return {
              ...amc,
              pdfTemplates: pdfData.templates || [],
              jsonTemplates: jsonData.templates || []
            };
          } catch {
            return { ...amc, pdfTemplates: [], jsonTemplates: [] };
          }
        })
      );
      setAmcs(amcsWithTemplates);

      // Set BAJAJ as default
      const bajaj = amcsWithTemplates.find(a => a.name === 'BAJAJ');
      if (bajaj && !selectedAmc) {
        setSelectedAmc(bajaj);
      } else if (amcsWithTemplates.length > 0 && !selectedAmc) {
        setSelectedAmc(amcsWithTemplates[0]);
      }
    } catch (err) {
      console.error('Error fetching AMCs:', err);
      setError('Failed to load AMCs');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async (amcId) => {
    try {
      const [pdfData, jsonData] = await Promise.all([
        apiCall(`/api/amcs/${amcId}/pdf-templates`),
        apiCall(`/api/amcs/${amcId}/json-templates`)
      ]);
      setPdfTemplates(pdfData.templates || []);
      setJsonTemplates(jsonData.templates || []);
      setUsingFallback(pdfData.fallback || jsonData.fallback || false);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates');
    }
  };

  // Admin handlers
  const handleCreateAmc = async () => {
    if (!newAmcName.trim()) {
      setError('AMC name is required');
      return;
    }
    setCreatingAmc(true);
    try {
      await apiCall('/api/amcs', {
        method: 'POST',
        body: JSON.stringify({
          name: newAmcName.trim(),
          description: newAmcDescription.trim()
        })
      });
      setSuccess(`AMC "${newAmcName}" created successfully`);
      setNewAmcName('');
      setNewAmcDescription('');
      setShowCreateModal(false);
      await fetchAmcs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to create AMC');
    } finally {
      setCreatingAmc(false);
    }
  };

  const handleUploadTemplate = async () => {
    if (!uploadFile || !uploadAmc) {
      setError('Please select file and AMC');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const endpoint = uploadType === 'pdf'
        ? `/api/amcs/${uploadAmc._id}/pdf-templates/upload`
        : `/api/amcs/${uploadAmc._id}/json-templates/upload`;
      const token = localStorage.getItem('token');
      const response = await apiCall(endpoint, {
        method: 'POST',
        body: formData,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }
      setSuccess(`${uploadType.toUpperCase()} uploaded successfully`);
      setUploadFile(null);
      setShowUploadModal(false);
      await fetchAmcs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAmc = async (amcId, amcName) => {
    try {
      await apiCall(`/api/amcs/${amcId}`, { method: 'DELETE' });
      setSuccess(`AMC "${amcName}" deleted`);
      setDeleteConfirm(null);
      await fetchAmcs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const handleDeleteTemplate = async (amcId, templateId, type) => {
    try {
      const endpoint = type === 'pdf'
        ? `/api/amcs/${amcId}/pdf-templates/${templateId}`
        : `/api/amcs/${amcId}/json-templates/${templateId}`;
      await apiCall(endpoint, { method: 'DELETE' });
      setSuccess(`${type.toUpperCase()} template deleted`);
      setDeleteConfirm(null);
      await fetchAmcs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  // Preview handlers
  const handlePdfPreview = async (template) => {
    try {
      const response = await fetch(`/api/amcs/${selectedAmc._id}/pdf-templates/${template._id}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setPreviewTemplate(template);
        setZoom(100);
      } else {
        setError('Failed to load PDF preview');
      }
    } catch (err) {
      setError('Failed to load PDF preview');
    }
  };

  const handleJsonPreview = async (template) => {
    try {
      const data = await apiCall(`/api/amcs/${selectedAmc._id}/json-templates/${template._id}/content`);
      if (data) {
        setJsonContent(data.content);
        setJsonPreview(template);
      }
    } catch (err) {
      setError('Failed to load JSON preview');
    }
  };

  const handlePdfDownload = async (template) => {
    try {
      const response = await fetch(`/api/amcs/${selectedAmc._id}/pdf-templates/${template._id}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = template.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const handleJsonDownload = async (template) => {
    try {
      const response = await apiCall(`/api/amcs/${selectedAmc._id}/json-templates/${template._id}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = template.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const closePdfPreview = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPreviewTemplate(null);
  };

  const closeJsonPreview = () => {
    setJsonContent(null);
    setJsonPreview(null);
  };

  const copyJsonToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(jsonContent, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));

  const formatDisplayName = (filename) => {
    return filename.replace(/\.(pdf|json)$/, '').replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  if (loading) {
    return (
      <div className="templates-page">
        <div className="page-header">
          <h1><FileText size={28} /> Templates & AMC Management</h1>
          <p>Manage and view all templates and extraction schemas</p>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="templates-page">
      <div className="page-header">
        <h1><FileText size={28} /> Templates & AMC Management</h1>
        <p>Manage and view all templates and extraction schemas</p>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {success && (
        <div className="success-message">
          <Check size={18} />
          <span>{success}</span>
        </div>
      )}

      {/* Admin Panel */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>AMC Management</h2>
          <button
            className="btn-toggle-admin"
            onClick={() => setShowAdminPanel(!showAdminPanel)}
          >
            {showAdminPanel ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>

        {showAdminPanel && (
          <div className="admin-content">
            <div className="admin-actions">
              <button className="btn-action" onClick={() => setShowCreateModal(true)}>
                <Plus size={16} /> Create AMC
              </button>
            </div>

            <div className="amcs-list">
              {amcs.map(amc => (
                <div key={amc._id} className="amc-card">
                  <div className="amc-card-header">
                    <div className="amc-info">
                      <h3>{amc.name}</h3>
                      {amc.description && <p>{amc.description}</p>}
                    </div>
                    <div className="amc-card-actions">
                      <button
                        className="btn-upload-small"
                        onClick={() => {
                          setUploadAmc(amc);
                          setShowUploadModal(true);
                        }}
                        title="Upload template"
                      >
                        <Upload size={14} /> Upload
                      </button>
                      {amc.name !== 'BAJAJ' && (
                        <button
                          className="btn-delete-small"
                          onClick={() => setDeleteConfirm({ type: 'amc', id: amc._id, name: amc.name })}
                          title="Delete AMC"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      <button
                        className="btn-expand-card"
                        onClick={() => setAdminExpandedAmc(adminExpandedAmc === amc._id ? null : amc._id)}
                      >
                        {adminExpandedAmc === amc._id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </div>
                  </div>

                  {adminExpandedAmc === amc._id && (
                    <div className="amc-card-content">
                      <div className="templates-section-admin">
                        <h4>📄 PDFs ({amc.pdfTemplates?.length || 0})</h4>
                        {amc.pdfTemplates && amc.pdfTemplates.length > 0 ? (
                          <div className="templates-list-admin">
                            {amc.pdfTemplates.map(t => (
                              <div key={t._id} className={`template-row ${amc.name === 'BAJAJ' ? 'protected' : ''}`}>
                                <span>{t.filename}</span>
                                {amc.name === 'BAJAJ' ? (
                                  <span className="badge-default">Default</span>
                                ) : (
                                  <button
                                    className="btn-delete-row"
                                    onClick={() => setDeleteConfirm({ type: 'pdf', id: t._id, name: t.filename, amcId: amc._id })}
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="no-items">No PDF templates</p>
                        )}
                      </div>

                      <div className="templates-section-admin">
                        <h4>📋 JSONs ({amc.jsonTemplates?.length || 0})</h4>
                        {amc.jsonTemplates && amc.jsonTemplates.length > 0 ? (
                          <div className="templates-list-admin">
                            {amc.jsonTemplates.map(t => (
                              <div key={t._id} className={`template-row ${amc.name === 'BAJAJ' ? 'protected' : ''}`}>
                                <span>{t.filename}</span>
                                {amc.name === 'BAJAJ' ? (
                                  <span className="badge-default">Default</span>
                                ) : (
                                  <button
                                    className="btn-delete-row"
                                    onClick={() => setDeleteConfirm({ type: 'json', id: t._id, name: t.filename, amcId: amc._id })}
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="no-items">No JSON schemas</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Template Viewer */}
      <div className="viewer-section">
        <div className="viewer-header">
          <h2>Browse Templates</h2>
          <div className="amc-selector">
            <label>AMC:</label>
            <select
              value={selectedAmc?._id || ''}
              onChange={(e) => {
                const amc = amcs.find(a => a._id === e.target.value);
                setSelectedAmc(amc);
              }}
            >
              {amcs.map(amc => (
                <option key={amc._id} value={amc._id}>{amc.name}</option>
              ))}
            </select>
          </div>
        </div>

        {usingFallback && (
          <div className="fallback-indicator">
            <AlertCircle size={16} />
            <span>Using BAJAJ default templates</span>
          </div>
        )}

        <div className="templates-tabs">
          <button className={`tab-btn ${activeTab === 'pdf' ? 'active' : ''}`} onClick={() => setActiveTab('pdf')}>
            <File size={18} /> PDF Templates ({pdfTemplates.length})
          </button>
          <button className={`tab-btn ${activeTab === 'json' ? 'active' : ''}`} onClick={() => setActiveTab('json')}>
            <Code size={18} /> JSON Schemas ({jsonTemplates.length})
          </button>
        </div>

        {activeTab === 'pdf' && (
          <div className="templates-grid">
            {pdfTemplates.map((template, index) => (
              <div key={index} className="template-card pdf-card">
                <div className="template-icon pdf-icon">
                  <File size={48} />
                  <span className="file-badge">PDF</span>
                </div>
                <div className="template-info">
                  <h3 className="template-name">{formatDisplayName(template.filename)}</h3>
                  <p className="template-filename">{template.filename}</p>
                  <p className="template-size">{formatFileSize(template.size)}</p>
                </div>
                <div className="template-actions">
                  <button className="btn-preview" onClick={() => handlePdfPreview(template)}>
                    <Eye size={18} /> Preview
                  </button>
                  <button className="btn-download" onClick={() => handlePdfDownload(template)}>
                    <Download size={18} />
                  </button>
                </div>
              </div>
            ))}
            {pdfTemplates.length === 0 && (
              <div className="empty-state">
                <File size={64} />
                <h3>No PDF Templates</h3>
              </div>
            )}
          </div>
        )}

        {activeTab === 'json' && (
          <div className="templates-grid">
            {jsonTemplates.map((template, index) => (
              <div key={index} className="template-card json-card">
                <div className="template-icon json-icon">
                  <Code size={48} />
                  <span className="file-badge json">JSON</span>
                </div>
                <div className="template-info">
                  <h3 className="template-name">{formatDisplayName(template.filename)}</h3>
                  <p className="template-filename">{template.filename}</p>
                  <p className="template-size">{formatFileSize(template.size)}</p>
                </div>
                <div className="template-actions">
                  <button className="btn-preview json-preview" onClick={() => handleJsonPreview(template)}>
                    <Eye size={18} /> Preview
                  </button>
                  <button className="btn-download" onClick={() => handleJsonDownload(template)}>
                    <Download size={18} />
                  </button>
                </div>
              </div>
            ))}
            {jsonTemplates.length === 0 && (
              <div className="empty-state">
                <Code size={64} />
                <h3>No JSON Schemas</h3>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Create New AMC</h2>
            <input
              type="text"
              placeholder="AMC Name"
              value={newAmcName}
              onChange={(e) => setNewAmcName(e.target.value)}
              disabled={creatingAmc}
            />
            <textarea
              placeholder="Description (optional)"
              value={newAmcDescription}
              onChange={(e) => setNewAmcDescription(e.target.value)}
              rows="3"
              disabled={creatingAmc}
            />
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowCreateModal(false)} disabled={creatingAmc}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreateAmc} disabled={creatingAmc || !newAmcName.trim()}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Upload to {uploadAmc?.name}</h2>
            <div className="type-selector">
              <button className={`type-btn ${uploadType === 'pdf' ? 'active' : ''}`} onClick={() => setUploadType('pdf')}>
                📄 PDF
              </button>
              <button className={`type-btn ${uploadType === 'json' ? 'active' : ''}`} onClick={() => setUploadType('json')}>
                📋 JSON
              </button>
            </div>
            <input
              type="file"
              accept={uploadType === 'pdf' ? '.pdf' : '.json'}
              onChange={(e) => setUploadFile(e.target.files?.[0])}
              disabled={uploading}
            />
            {uploadFile && <p className="file-info">Selected: {uploadFile.name}</p>}
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button className="btn-primary" onClick={handleUploadTemplate} disabled={uploading || !uploadFile}>
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content modal-danger" onClick={e => e.stopPropagation()}>
            <h2>Delete {deleteConfirm.type === 'amc' ? 'AMC' : deleteConfirm.type.toUpperCase()}?</h2>
            {deleteConfirm.type === 'amc' ? (
              <p>Delete AMC "{deleteConfirm.name}" and all its templates?</p>
            ) : (
              <p>Delete "{deleteConfirm.name}"?</p>
            )}
            <p className="warning">This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={() => {
                  if (deleteConfirm.type === 'amc') {
                    handleDeleteAmc(deleteConfirm.id, deleteConfirm.name);
                  } else {
                    handleDeleteTemplate(deleteConfirm.amcId, deleteConfirm.id, deleteConfirm.type);
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewTemplate && pdfUrl && (
        <div className="preview-modal-overlay" onClick={closePdfPreview}>
          <div className="preview-modal" onClick={e => e.stopPropagation()}>
            <div className="preview-header">
              <h3>{formatDisplayName(previewTemplate.filename)}</h3>
              <div className="preview-controls">
                <button onClick={handleZoomOut} disabled={zoom <= 50}>
                  <ZoomOut size={20} />
                </button>
                <span className="zoom-level">{zoom}%</span>
                <button onClick={handleZoomIn} disabled={zoom >= 200}>
                  <ZoomIn size={20} />
                </button>
                <button onClick={() => handlePdfDownload(previewTemplate)}>
                  <Download size={20} />
                </button>
                <button onClick={closePdfPreview} className="close-btn">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="preview-content">
              <div className="pdf-scroll-container">
                <iframe
                  src={`${pdfUrl}#toolbar=0&navpanes=0`}
                  title="PDF Preview"
                  className="pdf-iframe"
                  style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* JSON Preview Modal */}
      {jsonPreview && jsonContent && (
        <div className="preview-modal-overlay" onClick={closeJsonPreview}>
          <div className="preview-modal json-modal" onClick={e => e.stopPropagation()}>
            <div className="preview-header">
              <h3>{formatDisplayName(jsonPreview.filename)}</h3>
              <div className="preview-controls">
                <button onClick={copyJsonToClipboard} className={copied ? 'copied' : ''}>
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                </button>
                <button onClick={() => handleJsonDownload(jsonPreview)}>
                  <Download size={20} />
                </button>
                <button onClick={closeJsonPreview} className="close-btn">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="preview-content json-content">
              <pre className="json-pre">{JSON.stringify(jsonContent, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OriginalTemplates;
